
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Card, CardContent } from '@/components/ui/card';
import { MultipleDocumentUpload } from './document-upload/MultipleDocumentUpload';
import { MAX_DOCUMENTS_PER_FIELD } from './document-upload/types';

import type { Application } from '@/types/database.types';

interface DocumentUploadProps {
  data: Partial<Application>;
  onSave: (data: Partial<Application>) => void;
}

export const DocumentUpload = ({ data, onSave }: DocumentUploadProps) => {
  const [uploadedFiles, setUploadedFiles] = useState<{[key: string]: File[] | null}>({
    employee_census_file: null,
    dependent_census_file: null,
    cobra_census_file: null,
    workers_comp_declaration_file: null,
    workers_comp_loss_runs_file: null,
    health_insurance_renewal_file: null,
    sbc_file: null,
    tax_exempt_status_file: null,
    other_documents: null,
  });

  const { register, handleSubmit, reset, setValue, watch } = useForm<any>({
    defaultValues: {
      employee_census_file: Array.isArray(data.employee_census_file) ? data.employee_census_file : [],
      dependent_census_file: Array.isArray(data.dependent_census_file) ? data.dependent_census_file : [],
      cobra_census_file: Array.isArray((data as any).cobra_census_file) ? (data as any).cobra_census_file : [],
      workers_comp_declaration_file: Array.isArray(data.workers_comp_declaration_file) ? data.workers_comp_declaration_file : [],
      workers_comp_loss_runs_file: Array.isArray(data.workers_comp_loss_runs_file) ? data.workers_comp_loss_runs_file : [],
      health_insurance_renewal_file: Array.isArray(data.health_insurance_renewal_file) ? data.health_insurance_renewal_file : [],
      sbc_file: Array.isArray(data.sbc_file) ? data.sbc_file : [],
      tax_exempt_status_file: Array.isArray(data.tax_exempt_status_file) ? data.tax_exempt_status_file : [],
      other_documents: Array.isArray(data.other_documents) ? data.other_documents : [],
    },
  });

  useEffect(() => {
    reset({
      employee_census_file: Array.isArray(data.employee_census_file) ? data.employee_census_file : [],
      dependent_census_file: Array.isArray(data.dependent_census_file) ? data.dependent_census_file : [],
      cobra_census_file: Array.isArray((data as any).cobra_census_file) ? (data as any).cobra_census_file : [],
      workers_comp_declaration_file: Array.isArray(data.workers_comp_declaration_file) ? data.workers_comp_declaration_file : [],
      workers_comp_loss_runs_file: Array.isArray(data.workers_comp_loss_runs_file) ? data.workers_comp_loss_runs_file : [],
      health_insurance_renewal_file: Array.isArray(data.health_insurance_renewal_file) ? data.health_insurance_renewal_file : [],
      sbc_file: Array.isArray(data.sbc_file) ? data.sbc_file : [],
      tax_exempt_status_file: Array.isArray(data.tax_exempt_status_file) ? data.tax_exempt_status_file : [],
      other_documents: Array.isArray(data.other_documents) ? data.other_documents : [],
    });
  }, [data, reset]);

  const onSubmit = handleSubmit((formData) => {
    onSave(formData);
  });

  const handleFileUpload = (fieldName: string, files: File[]) => {
    setUploadedFiles(prev => ({ ...prev, [fieldName]: files }));
    // For now, we'll simulate URLs. In a real app, you'd upload files and get URLs back
    const mockUrls = files.map((file, index) => `mock-url-${fieldName}-${index}`);
    setValue(fieldName as keyof Application, mockUrls as any);
    onSubmit();
  };

  const handleFileRemove = (fieldName: string, index: number) => {
    const currentFiles = Array.isArray(data[fieldName as keyof Application]) 
      ? (data[fieldName as keyof Application] as string[])
      : [];
    const updatedFiles = currentFiles.filter((_, i) => i !== index);
    setValue(fieldName as keyof Application, updatedFiles as any);
    onSubmit();
  };

  const handleMultipleFileUpload = (fileUrls: string[]) => {
    setValue('other_documents', fileUrls);
    onSubmit();
  };

  const handleOtherDocumentRemove = (index: number) => {
    const currentUrls = Array.isArray(data.other_documents) ? data.other_documents : [];
    const updatedUrls = currentUrls.filter((_, i) => i !== index);
    handleMultipleFileUpload(updatedUrls);
  };

  return (
    <div className="space-y-6">
      <form onSubmit={onSubmit}>
        <Card>
          <CardContent className="pt-6 space-y-6">
            <div className="flex items-center gap-3 pb-2 border-b border-gray-100">
              <h2 className="text-2xl font-semibold">Document Upload</h2>
            </div>
            
            <div className="bg-primary/5 p-4 rounded-lg border border-primary/10 flex items-start gap-3">
              <p className="text-muted-foreground text-sm">
                Supporting documentation is necessary for the evaluation process and enables accurate proposals—while ensuring we have complete context for tailored recommendations.
              </p>
            </div>

          <div className="space-y-6">
            <MultipleDocumentUpload
              id="employee_census_file"
              label="Employee Census"
              maxDocuments={MAX_DOCUMENTS_PER_FIELD}
              currentFiles={Array.isArray(data.employee_census_file) ? data.employee_census_file : []}
              onUpload={(files) => handleFileUpload('employee_census_file', files)}
              onRemove={(index) => handleFileRemove('employee_census_file', index)}
            />

            <MultipleDocumentUpload
              id="dependent_census_file"
              label="Dependent Census"
              maxDocuments={MAX_DOCUMENTS_PER_FIELD}
              currentFiles={Array.isArray(data.dependent_census_file) ? data.dependent_census_file : []}
              onUpload={(files) => handleFileUpload('dependent_census_file', files)}
              onRemove={(index) => handleFileRemove('dependent_census_file', index)}
            />

            <MultipleDocumentUpload
              id="cobra_census_file"
              label="COBRA Census"
              maxDocuments={MAX_DOCUMENTS_PER_FIELD}
              currentFiles={Array.isArray((data as any).cobra_census_file) ? (data as any).cobra_census_file : []}
              onUpload={(files) => handleFileUpload('cobra_census_file', files)}
              onRemove={(index) => handleFileRemove('cobra_census_file', index)}
            />

            <MultipleDocumentUpload
              id="workers_comp_declaration_file"
              label="Workers' Comp Declaration Page"
              maxDocuments={MAX_DOCUMENTS_PER_FIELD}
              currentFiles={Array.isArray(data.workers_comp_declaration_file) ? data.workers_comp_declaration_file : []}
              onUpload={(files) => handleFileUpload('workers_comp_declaration_file', files)}
              onRemove={(index) => handleFileRemove('workers_comp_declaration_file', index)}
            />

            <MultipleDocumentUpload
              id="workers_comp_loss_runs_file"
              label="Workers' Comp Loss Runs (Last 5 Years)"
              maxDocuments={MAX_DOCUMENTS_PER_FIELD}
              currentFiles={Array.isArray(data.workers_comp_loss_runs_file) ? data.workers_comp_loss_runs_file : []}
              onUpload={(files) => handleFileUpload('workers_comp_loss_runs_file', files)}
              onRemove={(index) => handleFileRemove('workers_comp_loss_runs_file', index)}
            />

            <MultipleDocumentUpload
              id="health_insurance_renewal_file"
              label="Health Insurance Renewal"
              maxDocuments={MAX_DOCUMENTS_PER_FIELD}
              currentFiles={Array.isArray(data.health_insurance_renewal_file) ? data.health_insurance_renewal_file : []}
              onUpload={(files) => handleFileUpload('health_insurance_renewal_file', files)}
              onRemove={(index) => handleFileRemove('health_insurance_renewal_file', index)}
            />

            <MultipleDocumentUpload
              id="sbc_file"
              label="Summary of Benefits and Coverage (SBC)"
              maxDocuments={MAX_DOCUMENTS_PER_FIELD}
              currentFiles={Array.isArray(data.sbc_file) ? data.sbc_file : []}
              onUpload={(files) => handleFileUpload('sbc_file', files)}
              onRemove={(index) => handleFileRemove('sbc_file', index)}
            />

            <MultipleDocumentUpload
              id="tax_exempt_status_file"
              label="Tax Exempt Status"
              maxDocuments={MAX_DOCUMENTS_PER_FIELD}
              currentFiles={Array.isArray(data.tax_exempt_status_file) ? data.tax_exempt_status_file : []}
              onUpload={(files) => handleFileUpload('tax_exempt_status_file', files)}
              onRemove={(index) => handleFileRemove('tax_exempt_status_file', index)}
            />

            <MultipleDocumentUpload
              id="other_documents"
              label="Other Documents"
              maxDocuments={MAX_DOCUMENTS_PER_FIELD}
              currentFiles={data.other_documents}
              onUpload={(files) => {
                const currentUrls = Array.isArray(data.other_documents) ? data.other_documents : [];
                const mockUrls = files.map((file, index) => `mock-url-other-${currentUrls.length + index}`);
                handleMultipleFileUpload([...currentUrls, ...mockUrls]);
              }}
              onRemove={handleOtherDocumentRemove}
            />
          </div>
          </CardContent>
        </Card>
      </form>
    </div>
  );
};
